/*
 * structured-data.js
 * ------------------------------------------------------------
 * Builds schema.org JSON-LD from the store data fetched client-side
 * (the store list comes from the Storefront API, so most of this
 * information is not available to Liquid at render time).
 *
 * Exposes window.StructuredData. Add new builders here as more
 * structured-data types are needed (e.g. per-store LocalBusiness).
 *
 * Localized copy comes from window.mapStrings (set in head-init.liquid):
 *   storesIn          "Tiendas Brownie en [location]"  ([location] sentinel)
 *   storesAll         "Tiendas Brownie"
 *   storesDescription "Encuentra tu tienda Brownie más cercana. ..."
 */
window.StructuredData = (function () {
  // Inject (or replace) a JSON-LD <script> identified by schemaId, so the
  // same builder can run more than once without duplicating tags.
  function injectJsonLd(schemaId, data) {
    const selector = `script[type="application/ld+json"][data-schema="${schemaId}"]`;
    let el = document.head.querySelector(selector);

    if (!data) {
      if (el) el.remove();
      return;
    }

    if (!el) {
      el = document.createElement('script');
      el.type = 'application/ld+json';
      el.dataset.schema = schemaId;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
  }

  function fill(template, value) {
    return (template || '').replace('[location]', value);
  }

  // Some provinces are stored as abbreviations in the store data; expand them
  // to their full display name so the JSON-LD matches the visible city list
  // (see normalizeProvince in map-location.js). The ?city= filter normalizes
  // the same way, so the expanded name still resolves to the right stores.
  function normalizeProvince(province) {
    if (!province) return province;
    if (province.trim().toUpperCase() === 'PM') return 'Palma de Mallorca';
    return province;
  }

  // Absolute URL of the store-locator city page for a given province.
  function cityUrl(localizedRoot, province) {
    const root = localizedRoot || '/';
    return `${window.location.origin}${root}pages/localizacion-de-tiendas?city=${encodeURIComponent(province)}`;
  }

  function listPageUrl() {
    const canonical = document.querySelector('link[rel="canonical"]');
    return (canonical && canonical.href) || (window.location.origin + window.location.pathname);
  }

  // Rough price indicator per country (schema.org priceRange is free-form).
  const PRICE_RANGE = { ES: '€€', FR: '€€', PT: '€€', MX: '$$' };

  // Absolute URL of a single store's detail page on the current store.
  function storeUrl(localizedRoot, storeName) {
    const root = localizedRoot || '/';
    return `${window.location.origin}${root}pages/store-city?id=${encodeURIComponent(storeName)}`;
  }

  // Store names are inconsistent in the data: some already contain "Brownie",
  // others don't. Prepend the brand only when it is missing.
  function displayStoreName(rawName) {
    const name = (rawName || '').trim().replace(/\s+/g, ' ');
    return /brownie/i.test(name) ? name : `Brownie ${name}`;
  }

  function padTime(time) {
    const [h, m] = time.split(':');
    return `${h.padStart(2, '0')}:${m}`;
  }

  // Parse the stored hours string ('[["10:00 a 21:00"], ...]', Mon..Sun) into
  // schema.org OpeningHoursSpecification entries, grouping days that share the
  // same opening range. Supports split hours ("10:00 a 14:00 y 16:30 a 20:00").
  function parseOpeningHours(hoursJson) {
    if (!hoursJson) return null;

    let week;
    try {
      week = JSON.parse(hoursJson);
    } catch (e) {
      return null;
    }
    if (!Array.isArray(week)) return null;

    const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const groups = new Map(); // "opens|closes" -> { opens, closes, days: [] }

    week.forEach((entry, index) => {
      if (index > 6) return;
      const raw = Array.isArray(entry) ? entry[0] : entry;
      if (!raw || typeof raw !== 'string') return; // closed that day

      raw.split(/\s+y\s+/i).forEach(range => {
        const parts = range.split(/\s+a\s+/i);
        if (parts.length !== 2) return;
        const opens = parts[0].trim();
        const closes = parts[1].trim();
        if (!/^\d{1,2}:\d{2}$/.test(opens) || !/^\d{1,2}:\d{2}$/.test(closes)) return;

        const key = `${opens}|${closes}`;
        if (!groups.has(key)) {
          groups.set(key, { opens: padTime(opens), closes: padTime(closes), days: [] });
        }
        groups.get(key).days.push(DAYS[index]);
      });
    });

    const specs = Array.from(groups.values()).map(group => ({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: group.days,
      opens: group.opens,
      closes: group.closes,
    }));

    return specs.length ? specs : null;
  }

  function postalAddress(address, countryCode, includeRegion) {
    const pa = {
      '@type': 'PostalAddress',
      streetAddress: address.address1 || '',
      addressLocality: address.city || '',
      postalCode: address.zip || '',
    };
    if (includeRegion && address.province) pa.addressRegion = address.province;
    pa.addressCountry = countryCode || undefined;
    return pa;
  }

  // Build a schema.org ClothingStore object from a store record.
  function buildClothingStore(location, localizedRoot) {
    const address = location.address || {};
    const countryCode = (address.countryCode || '').toUpperCase();

    const store = {
      '@type': 'ClothingStore',
      name: displayStoreName(location.name),
      url: storeUrl(localizedRoot, location.name),
      priceRange: PRICE_RANGE[countryCode] || '€€',
      address: postalAddress(address, countryCode, false),
    };

    if (address.phone) store.telephone = address.phone;

    const openingHours = parseOpeningHours(address.hours);
    if (openingHours) store.openingHoursSpecification = openingHours;

    return store;
  }

  /**
   * Build an ItemList of store provinces and inject it as JSON-LD.
   *
   * Shows the stores in the current country (from the store data itself);
   * if there are none in the current country, shows the complete list.
   *
   * @param {Array} locations - store objects ({ address: { province, countryCode } })
   * @param {Object} [opts] - optional overrides (default to window.config / window.mapStrings)
   */
  function buildStoreItemList(locations, opts) {
    if (!Array.isArray(locations) || locations.length === 0) return;

    const options = opts || {};
    const config = window.config || {};
    const strings = options.strings || window.mapStrings || {};
    const country = (options.countryIsoCode || config.countryIsoCode || '').toUpperCase();
    const countryName = options.countryName || config.countryName || country;
    const localizedRoot = options.localizedRoot || window.Shopify?.routes?.root || '/';

    // Stores in the current country, derived from the data — no hardcoded
    // market list. Fall back to all stores when the current country has none.
    const inCountry = country
      ? locations.filter(loc => (loc.address?.countryCode || '').toUpperCase() === country)
      : [];
    const filtered = inCountry.length > 0;
    const list = filtered ? inCountry : locations;

    // Unique provinces, locale-sorted.
    const seen = new Set();
    const provinces = [];
    list.forEach(loc => {
      const province = normalizeProvince(loc.address?.province?.trim());
      if (province && !seen.has(province)) {
        seen.add(province);
        provinces.push(province);
      }
    });
    provinces.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

    if (provinces.length === 0) {
      injectJsonLd('store-item-list', null);
      return;
    }

    const itemListElement = provinces.map((province, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: fill(strings.storesIn, province),
      url: cityUrl(localizedRoot, province),
    }));

    injectJsonLd('store-item-list', {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: filtered ? fill(strings.storesIn, countryName) : strings.storesAll,
      description: strings.storesDescription,
      url: listPageUrl(),
      numberOfItems: itemListElement.length,
      itemListElement,
    });
  }

  /**
   * Build an ItemList of ClothingStore items for a single city/province
   * (the ?city= store-locator page) and inject it as JSON-LD.
   *
   * @param {Array} locations - the stores already filtered to the city
   * @param {Object} [opts]
   * @param {string} [opts.city] - fallback city name for the list title
   */
  function buildCityStoresList(locations, opts) {
    if (!Array.isArray(locations) || locations.length === 0) {
      injectJsonLd('city-store-list', null);
      return;
    }

    const options = opts || {};
    const strings = options.strings || window.mapStrings || {};
    const localizedRoot = options.localizedRoot || window.Shopify?.routes?.root || '/';

    const firstAddress = locations[0].address || {};
    const cityName = normalizeProvince((firstAddress.province || '').trim()) || options.city || firstAddress.city || '';

    const itemListElement = locations.map((location, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: buildClothingStore(location, localizedRoot),
    }));

    injectJsonLd('city-store-list', {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: fill(strings.storesIn, cityName),
      description: fill(strings.storesCityDescription, cityName),
      url: window.location.href,
      numberOfItems: itemListElement.length,
      itemListElement,
    });
  }

  function currentPageUrl() {
    const canonical = document.querySelector('link[rel="canonical"]');
    return (canonical && canonical.href) || window.location.href;
  }

  // Normalize a raw store name into a clean place name (drop the brand prefix
  // and " - " separators) for use as a ShoppingCenter name.
  function cleanCenterName(rawName) {
    return (rawName || '')
      .replace(/^brownie\s*-?\s*/i, '')
      .replace(/\s*-\s*/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // Heuristic: many Brownie points of sale sit inside a shopping centre or a
  // department store. Detect that from the store name (there is no explicit
  // field) and describe it as a containing ShoppingCenter.
  const MALL_PATTERN = /(el corte ingl[eé]s|centro comercial|\bc\.?\s?c\.?\b|\bcc\b|shopping|village|outlet)/i;
  function shoppingCenter(rawName, address, countryCode) {
    if (!MALL_PATTERN.test(rawName || '')) return null;
    return {
      '@type': 'ShoppingCenter',
      name: cleanCenterName(rawName),
      address: postalAddress(address, countryCode, false),
    };
  }

  /**
   * Build the full ClothingStore JSON-LD for a single store-detail page
   * (page.store-city, /pages/store-city?id=<name>) and inject it.
   *
   * @param {Object} location - the store record
   * @param {Object} [opts]
   * @param {Object} [opts.brand] - brand-level info (default window.brandInfo)
   */
  function buildStoreDetail(location, opts) {
    if (!location) {
      injectJsonLd('store-detail', null);
      return;
    }

    const options = opts || {};
    const brand = options.brand || window.brandInfo || {};
    const address = location.address || {};
    const countryCode = (address.countryCode || '').toUpperCase();
    const name = displayStoreName(location.name);
    const pageUrl = currentPageUrl();

    const store = {
      '@context': 'https://schema.org',
      '@type': 'ClothingStore',
      '@id': pageUrl,
      name,
      url: pageUrl,
    };

    if (brand.logo) store.logo = brand.logo;
    if (brand.email) store.email = brand.email;
    if (address.phone) store.telephone = address.phone;
    store.priceRange = PRICE_RANGE[countryCode] || '€€';

    if (Array.isArray(brand.sameAs) && brand.sameAs.length) {
      store.sameAs = brand.sameAs.filter(Boolean);
    }

    store.address = postalAddress(address, countryCode, true);

    const center = shoppingCenter(location.name, address, countryCode);
    if (center) store.containedInPlace = center;

    if (typeof address.latitude === 'number' && typeof address.longitude === 'number') {
      store.geo = {
        '@type': 'GeoCoordinates',
        latitude: address.latitude,
        longitude: address.longitude,
      };
    }

    const openingHours = parseOpeningHours(address.hours);
    if (openingHours) store.openingHoursSpecification = openingHours;

    const mapQuery = [name, address.city].filter(Boolean).join(' ');
    store.hasMap = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`;

    store.brand = { '@type': 'Brand', name: brand.name || 'Brownie' };
    if (brand.logo) store.brand.logo = brand.logo;
    if (brand.email) store.brand.email = brand.email;

    injectJsonLd('store-detail', store);
  }

  return {
    injectJsonLd,
    buildStoreItemList,
    buildCityStoresList,
    buildStoreDetail,
  };
})();
